import type { ActiveSession } from '../shared/types';
import { api, ApiError } from './api';
import { flushQueue, getActiveSession, getSessionRecord, persistWorkoutTransition, saveActiveSession, sessionHasPending, acceptRemoteSession } from './offline';

/** Every entry point resolves the original record, never a routine snapshot. */
export async function loadWorkoutRecord(profileId: string, id: string): Promise<ActiveSession> {
  let local = await getSessionRecord(profileId, id);
  if (navigator.onLine) {
    await flushQueue(profileId);
    local = await getSessionRecord(profileId, id);
    if (await sessionHasPending(profileId,id)) {
      if (local) return local;
      throw new Error('Your workout is saved locally and still waiting to sync.');
    }
    try {
      const remote = await api<ActiveSession>(`/records/${id}`, { headers: {'X-Hardnup-Profile': profileId} });
      return await acceptRemoteSession(profileId,remote,local);
    } catch (error) {
      if (error instanceof ApiError || !local) throw error;
    }
  }
  if (!local) throw new Error('Connect once to download this saved workout.');
  return local;
}

export async function resumeWorkoutRecord(profileId: string, id: string): Promise<ActiveSession> {
  const current = await getActiveSession(profileId);
  const target = await loadWorkoutRecord(profileId,id);
  if (!['active','partial'].includes(target.status ?? 'active')) throw new Error('This workout is closed. Its saved sets remain editable.');
  const resumeEventId=crypto.randomUUID();
  if (navigator.onLine) {
    const flushed = await flushQueue(profileId);
    const transportBlocked=Boolean(flushed.blocked?.lastError&&!/^\d{3}\s/.test(flushed.blocked.lastError));
    if (flushed.remaining&&!transportBlocked) throw new Error('Progress is saved on this phone. Reconnect and finish syncing before switching workouts.');
    if(!transportBlocked) {
      try {
        const response=await api<ActiveSession>(`/records/${id}/resume`, {method:'POST',headers:{'X-Hardnup-Profile':profileId},body:JSON.stringify({clientEventId:resumeEventId})});
        await acceptRemoteSession(profileId,response,target);
        const resumed=await loadWorkoutRecord(profileId,id);
        await saveActiveSession(profileId,resumed);
        return resumed;
      } catch(error) {
        // A network interface can stay connected while the server is unreachable.
        // HTTP auth/validation/conflict responses remain real errors, not offline consent.
        if(error instanceof ApiError) throw error;
        if(!(error instanceof TypeError)) throw error;
      }
    }
  }
  // Local snapshots and lifecycle actions are durable before opening another session.
  if (current && current.id !== id) {
    const eventId = crypto.randomUUID();
    await persistWorkoutTransition(profileId,{...current,status:'partial',pointer:{...current.pointer,restEndAt:null}},[
      {id:eventId,profileId,path:`/records/${current.id}/stop`,method:'POST',body:{clientEventId:eventId},createdAt:new Date().toISOString(),sequence:0},
    ]);
  }
  const resumed = {...target,status:'active',pointer:{...target.pointer,restEndAt:null}};
  await persistWorkoutTransition(profileId,resumed,[{id:resumeEventId,profileId,path:`/records/${id}/resume`,method:'POST',body:{clientEventId:resumeEventId},createdAt:new Date().toISOString(),sequence:1}]);
  return resumed;
}
